import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDisciplineStore } from '../contexts/disciplineStore';

/**
 * Stable Timer Updates Hook - Optimized for Smooth Display
 * Features:
 * - Stable 1-second intervals for consistent timer display (no flickering)
 * - Intelligent completion detection with celebration triggers
 * - Smooth progress tracking with optimized visual transitions
 * - Smart caching system to prevent excessive calculations
 * - Anti-flicker design with reduced update frequency conflicts
 * - Prevents timer jitter and provides stable, user-friendly experience
 */
export const useTimerUpdates = () => {
  const { 
    timers, 
    completeTimer, 
    pauseTimer, 
    resumeTimer,
    stopTimer 
  } = useDisciplineStore();

  const lastUpdateTime = useRef(0);
  const completedTimersRef = useRef(new Set());
  const cachingRef = useRef({});
  const updateIntervalRef = useRef(null);

  // Memoized active timers to prevent unnecessary recalculations
  const activeTimers = useMemo(() => 
    timers.filter(timer => 
      timer.isActive && 
      !timer.isCompleted && 
      !timer.isCancelled
    ), [timers]
  );
  // Calculate remaining time for a timer (optimized with improved caching)
  const calculateRemainingTime = useCallback((timer) => {
    if (!timer || timer.isCompleted || timer.isCancelled) return 0;
    
    const now = Date.now();
    const cacheKey = timer.id;      
    
    // Use cached time for frequent calls (prevent excessive calculations)
    // Cache valid for 800ms to reduce flickering while maintaining accuracy
    if (cachingRef.current[cacheKey] && now - cachingRef.current[cacheKey].timestamp < 800) {
      return cachingRef.current[cacheKey].value;
    }
    
    const startTime = new Date(timer.startTime).getTime();
    const duration = (timer.duration || 25) * 60 * 1000; // Convert to milliseconds
    const endTime = startTime + duration;
    
    // Account for paused time
    let totalPausedDuration = timer.totalPausedDuration || 0;
    if (timer.isPaused && timer.pauseTime) {
      totalPausedDuration += (now - new Date(timer.pauseTime).getTime());
    }
    
    const adjustedEndTime = endTime + totalPausedDuration;
    const remaining = Math.max(0, Math.floor((adjustedEndTime - now) / 1000));
    
    // Cache the result with timestamp
    cachingRef.current[cacheKey] = {
      value: remaining,
      timestamp: now
    };
    
    return remaining;
  }, []);
  // Auto-complete timers when they reach zero (optimized for stable display)
  useEffect(() => {
    const runningTimers = activeTimers.filter(timer => !timer.isPaused);

    if (runningTimers.length === 0) return;

    const checkTimers = () => {
      const now = Date.now();
      // Check every 500ms but only update if meaningful change occurred
      if (now - lastUpdateTime.current < 500) return;
      lastUpdateTime.current = now;
      
      runningTimers.forEach(timer => {
        // Skip if already processed to prevent duplicate completion
        if (completedTimersRef.current.has(timer.id)) return;
        
        const remainingTime = calculateRemainingTime(timer);
        
        if (remainingTime <= 0) {
          console.log('Timer completed automatically:', timer.goalName);
          
          // Mark as completed to prevent duplicate processing
          completedTimersRef.current.add(timer.id);
          
          // Show browser notification if permission granted
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Focus Session Completed!', {
              body: `${timer.goalName} - ${timer.duration} minutes completed!`,
              icon: '/favicon.ico',
              tag: `timer-${timer.id}`
            });
          }
          
          // Complete the timer
          completeTimer(timer.id);
          
          // Clean up after completion
          setTimeout(() => {
            completedTimersRef.current.delete(timer.id);
            delete cachingRef.current[timer.id];
          }, 3000);
        }
      });    };
      // More frequent checks (500ms) for completion detection but stable display
    const interval = setInterval(checkTimers, 500);
    
    // Initial check
    checkTimers();

    return () => {
      clearInterval(interval);
    };
  }, [activeTimers, completeTimer, calculateRemainingTime]);// Improved dependencies
  // Request notification permission on first use
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  return {
    calculateRemainingTime,
    activeTimers
  };
};

export default useTimerUpdates;
